/** @file CubeYawStops: fixed camera-yaw buttons on the orientation cube's ground ring (one per facing step). */
import type { ReactElement } from 'react';
import { ringPoint, type RingGeometry } from './ringPoint';
import { shortestYawDelta } from './shortestYawDelta';
import { VIEWPORT_COLORS } from './viewportColors';

/** The ring, how many stops (the puppet's `facing.steps`), and what a click does with a yaw in [0, 360). */
export type CubeYawStopsProps = RingGeometry & { steps: number; onPick: (yawDeg: number) => void };

/** Within this many degrees a stop counts as the current view (drawn filled). */
const CURRENT_DEG = 0.5;

/**
 * A dot per yaw `k x 360 / steps` where the camera would stand for it (bearing `180 - yaw`: yaw 0 stands south),
 * titled `camera yaw <deg>°`; a click sets that yaw and keeps the pitch. The current yaw's dot is filled.
 */
export function CubeYawStops(props: CubeYawStopsProps): ReactElement {
  const yaws = Array.from({ length: props.steps }, (_, k) => Math.round(((k * 360) / props.steps) * 100) / 100);
  return (
    <g data-testid="yaw-stops">
      {yaws.map((yaw) => {
        const [x, y] = ringPoint(props, 180 - yaw);
        const on = Math.abs(shortestYawDelta(props.yawDeg, yaw)) < CURRENT_DEG;
        return (
          <circle key={yaw} role="button" aria-label={`camera yaw ${String(yaw)}°`} cx={x} cy={y} r={on ? 3 : 2.2}
            fill={on ? VIEWPORT_COLORS.hover : '#141a17'} stroke={VIEWPORT_COLORS.hover} strokeWidth={1}
            style={{ cursor: 'pointer', pointerEvents: 'visiblePainted' }} onClick={() => { props.onPick(yaw); }}>
            <title>{`Camera yaw ${String(yaw)}°`}</title>
          </circle>
        );
      })}
    </g>
  );
}
